/**
 * useAssignedEntityIds — Two-hop scope resolution for entity filtering (DRR-075).
 *
 * Resolves which entities the current actor is assigned to via:
 *   1. ASSIGNED_TO edges: actorId → assignedActorIds (e.g., resident actor IDs)
 *   2. CORRELATES edges: residentActorId → externalEntityId (e.g., FHIR patient UUID)
 *
 * Uses backend scopeAssignments(actorId:) resolver (O(1) by actor).
 * DRR-102 (fail-closed): If no assignments exist, returns empty arrays.
 * Callers must check array length — empty = no access (not "show all").
 * Admin/architect callers should pass skip=true to bypass filtering entirely.
 */

import { useMemo } from 'react';
import { gql } from '@apollo/client';
import { useQuery } from '@apollo/client/react';

// ---------------------------------------------------------------------------
// GraphQL Operations
// ---------------------------------------------------------------------------

const SCOPE_ASSIGNMENTS_QUERY = gql`
  query ScopeAssignmentsForFilter($actorId: String!) {
    scopeAssignments(actorId: $actorId) {
      assignedActorIds
      correlatedEntityIds
    }
  }
`;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface UseAssignedEntityIdsProps {
  /** Current actor's sequential ID (e.g., '17_3') */
  actorId: string;
  /** Skip the query entirely (admin/architect views) */
  skip?: boolean;
}

export interface UseAssignedEntityIdsReturn {
  /**
   * FERP actor IDs this actor is assigned to (e.g., ['17_8', '17_9']).
   * null = skip was true (admin/architect bypass). Empty array = no assignments (show nothing).
   * DRR-102: Fail-closed — empty means "no access", NOT "all access".
   */
  assignedActorIds: string[] | null;
  /**
   * External entity IDs via CORRELATES (e.g., ['fhir:patient:{uuid}']).
   * null = skip was true or no CORRELATES edges. Empty array = no correlations.
   */
  correlatedEntityIds: string[] | null;
  isLoading: boolean;
  error: Error | null;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useAssignedEntityIds(
  props: UseAssignedEntityIdsProps,
): UseAssignedEntityIdsReturn {
  const { actorId, skip = false } = props;

  const {
    data,
    loading,
    error,
  } = useQuery<{
    scopeAssignments: {
      assignedActorIds: string[];
      correlatedEntityIds: string[];
    };
  }>(SCOPE_ASSIGNMENTS_QUERY, {
    variables: { actorId },
    skip: skip || !actorId,
  });

  const result = useMemo(() => {
    if (skip) {
      return { assignedActorIds: null, correlatedEntityIds: null };
    }

    const assignedActorIds = data?.scopeAssignments?.assignedActorIds ?? [];
    const correlatedEntityIds = data?.scopeAssignments?.correlatedEntityIds ?? [];

    // DRR-102 (fail-closed): no assignments = show nothing.
    if (assignedActorIds.length === 0) {
      return { assignedActorIds: [], correlatedEntityIds: [] };
    }

    return {
      assignedActorIds,
      correlatedEntityIds: correlatedEntityIds.length > 0 ? correlatedEntityIds : null,
    };
  }, [actorId, data, skip]);

  return {
    ...result,
    isLoading: loading,
    error: error ?? null,
  };
}
